import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { IAuthSignup, IAuthToken } from '@/types/models/auth';
import { IQuota } from '@/types/models/quota';
import { ISubscription } from '@/types/models/subscription';
import { IUser } from '@/types/models/user';
import { IAuthChangePasswordParams, IAuthParams, IAuthResetPasswordParams, ISignupParams } from '@/types/params/auth';
import { isPresent } from '@/utils/lang';
import { clearLocalUser, loadLocalUser, storeLocalUser, storeLanguage, loadLanguage } from '@/utils/local_storage';
import * as AuthService from '@/services/auth';
import { createCustomerSession } from '@/services/stripe';
import { getApiSubscription, getMe, getUserQuota, getUserSubscription, updateUserProfile } from '@/services/user';
import { i18n } from '@/locales/i18n';

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    loginByProvider: '',
    isLocalUserRestored: false as boolean,
    currentUser: undefined as IAuthToken | undefined,
    currentProfile: {} as IUser,
    currentSubscription: undefined as ISubscription | undefined,
    currentApiSubscription: undefined as ISubscription | undefined,
    currentQuota: undefined as IQuota | undefined,
    userSignupEmail: '' as string,
    userSignupPassword: '' as string,
    invitationState: {
      hasInvitation: false,
      code: '',
      teamName: '',
      teamId: '',
      role: '',
      invitedEmail: '',
    } as {
      hasInvitation: boolean;
      code: string;
      teamName: string;
      teamId: string;
      role: string;
      invitedEmail: string;
    },
  }),
  getters: {
    isLoginByGoogle(state) {
      return isPresent(state.loginByProvider) && state.loginByProvider === 'google';
    },
    isSignedIn(state) {
      return isPresent(state.currentUser) && isPresent(state.currentUser?.accessToken);
    },
    isCurrentSubscriptionFree(state) {
      return state.currentSubscription?.plan?.tier === 'FREE' || false;
    },
    getCurrentUser(state) {
      return state.currentUser;
    },
    getCurrentProfile(state) {
      return state.currentProfile;
    },
    getCurrentSubscription(state) {
      return state.currentSubscription;
    },
    getCurrentApiSubscription(state) {
      return state.currentApiSubscription;
    },
    getCurrentQuota(state) {
      return state.currentQuota;
    },
    getCurrentUserSignupEmail(state) {
      return state.userSignupEmail;
    },
    getCurrentUserSignupPassword(state) {
      return state.userSignupPassword;
    },
    hasUserProfileAndTeam(state) {
      return (
        isPresent(state.currentProfile.name) &&
        isPresent(state.currentProfile.teams) &&
        isPresent(state.currentProfile.currentTeam?.name)
      );
    },
    hasOnlyUserProfile(state) {
      return isPresent(state.currentProfile.name);
    },
    getInvitationState(state) {
      return state.invitationState;
    },
    getNextRenewalDate(state) {
      if (this.isCurrentSubscriptionFree) return null;

      if (!state.currentSubscription || !state.currentSubscription?.currentPeriodStart) return null;

      const today = DateTime.now();
      const subStartDate = DateTime.fromISO(state.currentSubscription.currentPeriodStart, { zone: 'utc' }).startOf(
        'day'
      );
      const diff = today.diff(subStartDate, ['years', 'months']).toObject();
      const monthsDiff = (diff.years || 0) * 12 + (diff.months || 0);
      const periodStart = subStartDate.plus({ months: monthsDiff });
      const periodEnd = periodStart.plus({ months: 1 });
      return periodEnd;
    },
  },
  actions: {
    /**
     * Ensure the user has a Stripe Customer ID, creating one if missing.
     */
    async ensureStripeCustomer(): Promise<string | undefined> {
      const profile = this.currentProfile;
      if (!profile.stripeCustomerId) {
        try {
          const id = await createCustomerSession(profile.email, profile.id);
          profile.stripeCustomerId = id;
        } catch (e) {
          console.error('Failed to ensure Stripe customer:', e);
          return undefined;
        }
      }
      return profile.stripeCustomerId;
    },
    setLoginByProvider(provider: string) {
      this.loginByProvider = provider;
    },
    setCurrentUser(user: IAuthToken) {
      this.currentUser = user;
      storeLocalUser(user);
    },
    setCurrentUserSignupEmail(userEmail: string) {
      this.userSignupEmail = userEmail;
    },
    setCurrentUserSignupPassword(userPassword: string) {
      this.userSignupPassword = userPassword;
    },
    setCurrentProfile(currentProfile: IUser) {
      this.currentProfile = currentProfile;
    },
    setCurrentSubscription(subscription: ISubscription) {
      this.currentSubscription = subscription;
    },
    setCurrentApiSubscription(subscription: ISubscription) {
      this.currentApiSubscription = subscription;
    },
    setCurrentQuota(quota: IQuota) {
      this.currentQuota = quota;
    },
    clearCurrentUser() {
      this.currentUser = undefined;
      this.currentSubscription = undefined;
      this.currentApiSubscription = undefined;
      this.currentQuota = undefined;
      this.currentProfile = {} as IUser;
      this.loginByProvider = '';
      this.isLocalUserRestored = false;
      this.userSignupEmail = '';
      this.userSignupPassword = '';
      this.invitationState = {
        hasInvitation: false,
        code: '',
        teamName: '',
        teamId: '',
        role: '',
        invitedEmail: '',
      };
      clearLocalUser();
    },
    setInvitationState(invitation: {
      code: string;
      teamName: string;
      teamId: string;
      role: string;
      invitedEmail?: string;
    }) {
      this.invitationState = {
        hasInvitation: true,
        code: invitation.code,
        teamName: invitation.teamName,
        teamId: invitation.teamId,
        role: invitation.role,
        invitedEmail: invitation.invitedEmail || '',
      };
    },
    clearInvitationState() {
      this.invitationState = {
        hasInvitation: false,
        code: '',
        teamName: '',
        teamId: '',
        role: '',
        invitedEmail: '',
      };
    },
    async login(userData: IAuthParams): Promise<IAuthToken> {
      const user = await AuthService.login(userData);
      if (!user || !user.accessToken) throw user;

      this.setCurrentUser(user);
      await this.fetchMe();
      return user;
    },

    async googleLogin(formData: { access_token: string }): Promise<IAuthToken> {
      const user = await AuthService.googleLogin(formData);
      if (!user || !user.accessToken) throw user;

      this.setCurrentUser(user);
      await this.fetchMe();
      return user;
    },
    restoreLocalUser() {
      return new Promise((resolve, reject) => {
        if (this.isLocalUserRestored) return resolve({});

        const user = loadLocalUser();
        if (user && user.accessToken) {
          this.setCurrentUser(user);
          this.isLocalUserRestored = true;
          resolve(user);
        } else {
          this.clearCurrentUser();
          reject({});
        }
      });
    },
    async fetchUserProfile() {
      try {
        const resp = await AuthService.userProfile();
        this.currentProfile = resp.data as IUser;
        this.loginByProvider = this.currentProfile.provider || '';

        // Check if user changed language on login page (before logging in)
        const localLanguage = loadLanguage();

        if (localLanguage && localLanguage !== this.currentProfile.language) {
          // User selected a different language on login page - respect their choice
          try {
            await updateUserProfile({ language: localLanguage });
            this.currentProfile.language = localLanguage;
            i18n.global.locale.value = localLanguage;
            storeLanguage(localLanguage);
          } catch (error) {
            console.error('Failed to update profile language on login:', error);
            // Fallback to profile language if update fails
            i18n.global.locale.value = this.currentProfile.language;
            storeLanguage(this.currentProfile.language);
          }
        } else {
          // Use profile language
          i18n.global.locale.value = this.currentProfile.language;
          storeLanguage(this.currentProfile.language);
        }
      } catch {
        this.currentProfile = {} as IUser;
      }
    },
    async fetchUserSubscription() {
      try {
        const res = await getUserSubscription();
        if (res.data) {
          this.setCurrentSubscription(res.data);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async fetchApiSubscription() {
      try {
        const res = await getApiSubscription();
        // Handle the new response structure with hasApiSubscription field
        if (res.data && res.data.hasApiSubscription && res.data.subscription) {
          this.setCurrentApiSubscription(res.data.subscription);
        } else {
          // User has no API subscription, clear the current API subscription
          this.currentApiSubscription = undefined;
        }
      } catch (e) {
        console.error(e);
        // Set to undefined in case of error
        this.currentApiSubscription = undefined;
      }
    },
    async fetchUserQuota() {
      try {
        const res = await getUserQuota();
        if (res.data) {
          this.setCurrentQuota(res.data?.quota || ({} as IQuota));
        }
      } catch (e) {
        console.error(e);
      }
    },
    async fetchMe() {
      try {
        const res = await getMe();
        if (!res.data) return;

        const { profile, subscription, apiSubscription, quota } = res.data;

        this.currentProfile = profile;
        this.loginByProvider = profile.provider || '';
        const localLanguage = loadLanguage();
        if (localLanguage && localLanguage !== profile.language) {
          try {
            await updateUserProfile({ language: localLanguage });
            this.currentProfile.language = localLanguage;
            i18n.global.locale.value = localLanguage;
            storeLanguage(localLanguage);
          } catch {
            i18n.global.locale.value = profile.language;
            storeLanguage(profile.language);
          }
        } else {
          i18n.global.locale.value = profile.language;
          storeLanguage(profile.language);
        }

        if (subscription) this.setCurrentSubscription(subscription);

        if (apiSubscription.hasApiSubscription && apiSubscription.subscription) {
          this.setCurrentApiSubscription(apiSubscription.subscription);
        } else {
          this.currentApiSubscription = undefined;
        }

        if (quota) this.setCurrentQuota(quota);
      } catch (e) {
        console.error(e);
      }
    },
    async verifyUserToken(): Promise<IAuthToken> {
      try {
        const user = await AuthService.auth();
        if (!user || !user.accessToken) throw user;

        this.setCurrentUser(user);
        await this.fetchMe();
        return user;
      } catch (error) {
        this.clearCurrentUser();
        throw error;
      }
    },
    logout() {
      this.clearCurrentUser();
    },
    resetPassword(formData: IAuthResetPasswordParams) {
      return AuthService.resetPassword(formData);
    },
    forgotPassword(formData: { email: string; language?: string }) {
      return AuthService.forgotPassword(formData);
    },
    async checkEmail(formData: { email: string }) {
      const resp = await AuthService.checkEmail(formData);
      return resp.data;
    },
    sendVerificationCode(formData: { email: string; language?: string }) {
      return AuthService.sendVerificationCode(formData);
    },
    async resendCode(formData: { email: string; language?: string }) {
      const resp = await AuthService.resendCode(formData);
      return resp.data;
    },
    verifyCode(formData: { email: string; otp: string }) {
      return AuthService.verifyCode(formData);
    },
    async signup(userData: ISignupParams): Promise<IAuthSignup> {
      const user = await AuthService.signup(userData);
      if (!user || !user.token) throw user;

      this.setCurrentUser({
        accessToken: user.token,
        tokenType: 'Bearer',
      });

      // ここでは、まだチームを作ってないからquotaやサブスクリプションは取得できない
      await Promise.all([this.fetchUserProfile()]);
      return user;
    },
    async googleSignup(formData: { access_token: string }): Promise<IAuthToken> {
      const user = await AuthService.googleSignup(formData);
      if (!user || !user.accessToken) throw user;

      this.setCurrentUser(user);
      await Promise.all([this.fetchUserProfile()]);
      return user;
    },
    changePassword(formData: IAuthChangePasswordParams) {
      return AuthService.changePassword(formData);
    },
  },
});
